import { createApp, defineAsyncComponent } from 'vue'
import {createWebHashHistory, createMemoryHistory, createRouter } from 'vue-router'
import ui from '@nuxt/ui/vue-plugin'

// Bugsnag — lazy-loaded, not needed for initial render
if (window.CursusGeregeld.bugsnag) {
    Promise.all([
        import('@bugsnag/js'),
        import('@bugsnag/plugin-vue'),
    ]).then(([{ default: Bugsnag }, { default: BugsnagPluginVue }]) => {
        Bugsnag.start({
            apiKey: window.CursusGeregeld.bugsnag,
            plugins: [new BugsnagPluginVue()]
        })
    })
}

import { i18nVue } from 'laravel-vue-i18n'
import { Settings } from 'luxon'
import mitt from 'mitt'

Settings.defaultLocale = 'nl-NL'
Settings.defaultZone = 'Europe/Amsterdam'

// Critical components — needed on every page for initial render
import App from './components/App.vue'
import Toast from './components/Toast.vue'
import Backoffice from './components/Backoffice.vue'
import Navigation from './components/Navigation.vue'
import Logo from './components/Logo.vue'
import LogoIcon from './components/LogoIcon.vue'
import Icon from './components/Icon.vue'
import ProfileButton from './components/ProfileButton.vue'
import CartBasket from './components/CartBasket.vue'

// Async components — loaded on demand per page
const AutocompleteBar = defineAsyncComponent(() => import('./components/AutocompleteBar.vue'))
const BaseModal = defineAsyncComponent(() => import('./components/BaseModal.vue'))
const CartHasProducts = defineAsyncComponent(() => import('./components/CartHasProducts.vue'))
const Rating = defineAsyncComponent(() => import('./components/Rating.vue'))
const FlagIcon = defineAsyncComponent(() => import('./components/FlagIcon.vue'))
const StudentSelector = defineAsyncComponent(() => import('./components/StudentSelector.vue'))
const CartDetail = defineAsyncComponent(() => import('./components/CartDetail.vue'))
const AddToCart = defineAsyncComponent(() => import('./components/AddToCart.vue'))
const RegisterStudents = defineAsyncComponent(() => import('./components/RegisterStudents.vue'))
const Money = defineAsyncComponent(() => import('./components/Money.vue'))
const Order = defineAsyncComponent(() => import('./components/Order.vue'))
const BundleNavigation = defineAsyncComponent(() => import('./components/BundleNavigation.vue'))
const Bundles = defineAsyncComponent(() => import('./components/Bundles.vue'))
const BundleHeader = defineAsyncComponent(() => import('./components/bundle/Header.vue'))
const BundleFilters = defineAsyncComponent(() => import('./components/bundle/Filters.vue'))
const BundleResults = defineAsyncComponent(() => import('./components/bundle/Results.vue'))
const BundleSearch = defineAsyncComponent(() => import('./components/bundle/Search.vue'))
const BundleInfoNav = defineAsyncComponent(() => import('./components/bundle/InfoNav.vue'))
const ScheduleFilterBar = defineAsyncComponent(() => import('./components/schedule/FilterBar.vue'))
const ScheduleResults = defineAsyncComponent(() => import('./components/schedule/Results.vue'))
const ScheduleHeader = defineAsyncComponent(() => import('./components/schedule/Header.vue'))
const ScheduleSearch = defineAsyncComponent(() => import('./components/schedule/Search.vue'))
const SupplierRequest = defineAsyncComponent(() => import('./components/supplier/Request.vue'))
const Account = defineAsyncComponent(() => import('./components/account/Account.vue'))
const Proposal = defineAsyncComponent(() => import('./components/proposal/Proposal.vue'))
const StudentModal = defineAsyncComponent(() => import('./components/StudentModal.vue'))
const NewsletterPopup = defineAsyncComponent(() => import('./components/NewsletterPopup.vue'))

const app = createApp({})

app.use(ui)

app.use(i18nVue, {
    resolve: async lang => {
        const langs = import.meta.glob('../../../../lang/*.json')
        return await langs[`../../../../lang/${lang}.json`]()
    }
})

import bundleStore from './stores/bundleStore.js'
import userStore from './stores/userStore.js'
import cartStore from './stores/cartStore.js'
import scheduleStore from './stores/scheduleStore.js'

userStore.loadUser()
cartStore.loadCart()

app.provide('cartStore', cartStore);
app.provide('userStore', userStore);
app.provide('bundleStore', bundleStore);
app.provide('scheduleStore', scheduleStore);

const emitter = mitt()
app.provide('emitter', emitter)

// General
app.component('cg-app', App)
app.component('bundle-navigation', BundleNavigation)
app.component('autocomplete-bar', AutocompleteBar)
app.component('base-modal', BaseModal)
app.component('profile-button', ProfileButton)
app.component('cart-has-products', CartHasProducts)
app.component('toast', Toast)
app.component('backoffice', Backoffice)
app.component('newsletter-popup', NewsletterPopup)
app.component('flag-icon', FlagIcon)
app.component('rating', Rating)
app.component('icon', Icon)
app.component('logo-icon', LogoIcon)
app.component('logo', Logo)
app.component('order', Order)
app.component('money', Money)
app.component('navigation', Navigation)

// Proposal
app.component('cg-proposal', Proposal)
app.component('cg-student-modal', StudentModal)

// Cart
app.component('add-to-cart', AddToCart)
app.component('cart-basket', CartBasket)
app.component('cart-detail', CartDetail)

// Course
app.component('cg-student-selector', StudentSelector)
app.component('register-students', RegisterStudents)

//
app.component('bundles', Bundles)

// Bundle
app.component('bundle-search', BundleSearch)
app.component('bundle-info-nav', BundleInfoNav)
app.component('bundle-header', BundleHeader)
app.component('bundle-filters', BundleFilters)
app.component('bundle-results', BundleResults)

// Schedule
app.component('schedule-search', ScheduleSearch)
app.component('schedule-header', ScheduleHeader)
app.component('schedule-filter-bar', ScheduleFilterBar)
app.component('schedule-results', ScheduleResults)

// Supplier
app.component('supplier-request', SupplierRequest)

// Account
app.component('account', Account)

if (window.location.pathname.startsWith('/account')) {
    const router = createRouter({
        history: createWebHashHistory('/account'),
        routes: [
            { path: '/', component: () => import('./components/account/Dashboard.vue') },
            { path: '/boekingen', component: () => import('./components/account/Orders.vue') },
            { path: '/boekingen/:orderId', component: () => import('./components/account/Order.vue') },
            { path: '/certificaten', component: () => import('./components/account/Certificates.vue') },
            { path: '/cursisten/:studentId', component: () => import('./components/account/Student.vue') },
            { path: '/cursisten', component: () => import('./components/account/Students.vue') },
            { path: '/profiel', component: () => import('./components/account/Profile.vue') },
            { path: '/bedrijf', component: () => import('./components/account/Employer.vue') },
            { path: '/uitloggen', component: () => import('./components/account/AccountLogout.vue') },
        ],
    })
    app.use(router)
}  else {
    // Buiten /account is er geen routing, maar Nuxt UI-links verwachten wel een router.
    // De catch-all is nodig omdat createMemoryHistory() op locatie '' start: zonder
    // matchende route waarschuwt vue-router met `No match found for location with path ""`.
    const router = createRouter({
        history: createMemoryHistory(),
        routes: [
            { path: '/:pathMatch(.*)*', component: { render: () => null } },
        ],
    })
    app.use(router)
}

window.CursusGeregeld.App = app.mount('#app')

window.CursusGeregeld.lockBody = function () {
    if (!document.body.classList.contains('overflow-hidden')) {
        document.body.classList.add('overflow-hidden')
    }
}

window.CursusGeregeld.unlockBody = function () {
    document.body.classList.remove('overflow-hidden')
}

window.CursusGeregeld.toggleBody = function () {
    if (!document.body.classList.contains('overflow-hidden')) {
        document.body.classList.add('overflow-hidden')
    } else {
        document.body.classList.remove('overflow-hidden')
    }
}

document.addEventListener('DOMContentLoaded', function () {
    if (window.location.hash === '#resultaten' && document.querySelector('#resultaten')) {
        window.scrollToElement('#resultaten')
    }
});
